import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, FlaskConical, Pill, ReceiptText, FileText, Plus, LogOut, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader } from "@/components/Loader";
import { EmptyState } from "@/components/EmptyState";
import { StatusBadge } from "@/components/StatusBadge";
import { PWAInstallBanner } from "@/components/PWAInstall";
import { money, fmtDateTime, fmtDate, initials, timeAgo } from "@/lib/format";
import { logoSrc } from "@/lib/api";

const TABS = [
  { key: "appointments", label: "Appointments", icon: CalendarDays },
  { key: "results", label: "Lab Results", icon: FlaskConical },
  { key: "prescriptions", label: "Prescriptions", icon: Pill },
  { key: "invoices", label: "Billing", icon: ReceiptText },
  { key: "documents", label: "Documents", icon: FileText },
];

export default function PatientPortal() {
  const { user, logout } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState("appointments");
  const hospitals = useQuery({ queryKey: ["portal-hospitals"], queryFn: async () => (await api.get("/portal/hospitals")).data || [] });
  const orgLogo = (hospitals.data || []).find((h) => h.id === (user?.org_id || user?.org?.id))?.logo || "";
  useEffect(() => {
    let link = document.querySelector("link[rel='icon']");
    if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
    link.href = orgLogo ? logoSrc(orgLogo) : "/favicon-32.png";
  }, [orgLogo]);
  const [bookOpen, setBookOpen] = useState(false);
  const [book, setBook] = useState({ date: "", reason: "" });

  const appts = useQuery({ queryKey: ["portal", "appointments"], queryFn: async () => (await api.get("/portal/appointments")).data || [] });
  const results = useQuery({ queryKey: ["portal", "results"], queryFn: async () => (await api.get("/portal/results")).data || [] });
  const rx = useQuery({ queryKey: ["portal", "prescriptions"], queryFn: async () => (await api.get("/portal/prescriptions")).data || [] });
  const invoices = useQuery({ queryKey: ["portal", "invoices"], queryFn: async () => (await api.get("/portal/invoices")).data || [] });
  const docs = useQuery({ queryKey: ["portal", "documents"], queryFn: async () => (await api.get("/portal/documents")).data || [] });

  const bookMut = useMutation({
    mutationFn: async () => (await api.post("/portal/appointments", { date: book.date ? new Date(book.date).toISOString() : null, reason: book.reason })).data,
    onSuccess: () => { toast.success("Appointment requested"); qc.invalidateQueries({ queryKey: ["portal", "appointments"] }); setBookOpen(false); setBook({ date: "", reason: "" }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const dataFor = { appointments: appts, results, prescriptions: rx, invoices, documents: docs }[tab];

  const renderList = () => {
    if (dataFor.isLoading) return <Loader />;
    const rows = dataFor.data || [];
    if (!rows.length) return <EmptyState title={`No ${tab} yet`} description="This will update as your care team adds information." testId="portal-empty" />;
    return (
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={r.id || i} data-testid={`portal-${tab}-${i}`} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-800">
                {tab === "appointments" && (r.reason || "Appointment")}
                {tab === "results" && (r.test_name || "Lab result")}
                {tab === "prescriptions" && `${r.drug || "Medication"} ${r.dosage || ""}`}
                {tab === "invoices" && (r.invoice_number || `Invoice ${r.id?.slice(0, 8)}`)}
                {tab === "documents" && (r.name || r.title || "Document")}
              </p>
              <p className="text-xs text-slate-500">
                {tab === "appointments" && `${r.provider || ""} · ${fmtDateTime(r.date)}`}
                {tab === "results" && (r.specimen || fmtDate(r.created_at))}
                {tab === "prescriptions" && `${r.frequency || ""} ${r.duration || ""}`}
                {tab === "invoices" && <span className="font-mono">{money(r.total)}</span>}
                {tab === "documents" && timeAgo(r.created_at)}
              </p>
            </div>
            {r.status && <StatusBadge value={r.status} />}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <header className="sticky top-[var(--ticker-h,0px)] z-30 border-b border-slate-200 glass">
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-4">
          <div className="flex items-center gap-3">
            {orgLogo
              ? <img src={logoSrc(orgLogo)} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="h-9 w-9 rounded-lg border border-slate-200 bg-white object-contain p-0.5" />
              : <span className="flex h-9 w-9 items-center justify-center rounded-full bg-teal-700 text-sm font-bold text-white">{initials(user?.full_name)}</span>}
            <div><p className="text-sm font-semibold text-slate-900">{user?.full_name}</p><p className="text-xs text-slate-500">Patient Portal</p></div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" data-testid="portal-book" className="gap-1.5 bg-teal-700 hover:bg-teal-800" onClick={() => setBookOpen(true)}><CalendarPlus className="h-4 w-4" /> Book</Button>
            <button data-testid="portal-logout" onClick={logout} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><LogOut className="h-5 w-5" /></button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-6">
        <div className="animate-fade-up">{renderList()}</div>
      </main>

      {/* Bottom tab bar */}
      <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white safe-bottom">
        <div className="mx-auto grid max-w-3xl grid-cols-5">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`portal-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition-colors ${tab === t.key ? "text-teal-700" : "text-slate-400"}`}>
              <t.icon className="h-5 w-5" /> {t.label}
            </button>
          ))}
        </div>
      </nav>

      <Dialog open={bookOpen} onOpenChange={setBookOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">Book an appointment</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Preferred date & time</Label><Input data-testid="book-date" type="datetime-local" value={book.date} onChange={(e) => setBook({ ...book, date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Reason</Label><Textarea data-testid="book-reason" value={book.reason} onChange={(e) => setBook({ ...book, reason: e.target.value })} rows={3} placeholder="Describe your symptoms or reason for visit" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBookOpen(false)}>Cancel</Button>
            <Button data-testid="book-submit" disabled={bookMut.isPending} className="bg-teal-700 hover:bg-teal-800" onClick={() => bookMut.mutate()}>{bookMut.isPending ? "Requesting…" : "Request appointment"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PWAInstallBanner />
    </div>
  );
}
